/**
 * CustomerTable Component
 * Displays a table of individual taxpayer customers with selection, sorting, and actions
 */

import { useNavigate } from 'react-router-dom'
import {
  ChevronUp,
  ChevronDown,
  User,
} from 'lucide-react'
import type { IndividualCustomer } from '@/types/customer'
import { useCustomerStore } from '@/store/customerStore'

interface CustomerTableProps {
  customers: IndividualCustomer[]
  onCustomerClick?: (id: string) => void
}

export default function CustomerTable({ customers, onCustomerClick }: CustomerTableProps) {
  const navigate = useNavigate()
  const {
    selectedIds,
    toggleSelectCustomer,
    selectAllCustomers,
    clearSelection,
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder,
  } = useCustomerStore()

  const allSelected = customers.length > 0 && customers.every((c) => selectedIds.includes(c.id))
  const someSelected = customers.some((c) => selectedIds.includes(c.id)) && !allSelected

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection()
    } else {
      selectAllCustomers(customers.map((c) => c.id))
    }
  }

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
  }

  const handleRowClick = (id: string) => {
    if (onCustomerClick) {
      onCustomerClick(id)
    } else {
      navigate(`/customers/${id}`)
    }
  }

  const SortIcon = ({ column }: { column: typeof sortBy }) => {
    if (sortBy !== column) return null
    return sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    )
  }

  const getStatusBadge = (status: IndividualCustomer['status']) => {
    const styles = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
      archived: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    }
    const labels = {
      active: 'Active',
      inactive: 'Inactive',
      archived: 'Archived',
    }
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    )
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  if (customers.length === 0) {
    return (
      <div className="card card-elevated p-12 text-center">
        <div className="w-16 h-16 bg-surface-overlay rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-text-tertiary" />
        </div>
        <p className="text-lg font-medium text-text-primary mb-2">No customers found</p>
        <p className="text-text-secondary">Try adjusting your search or filters</p>
      </div>
    )
  }

  return (
    <div className="card card-elevated overflow-hidden">
      <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
        <table className="table-sticky-head w-full">
          <thead className="bg-surface-overlay">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected
                  }}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
                />
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-surface-light transition-colors"
                onClick={() => handleSort('name')}
              >
                Customer <SortIcon column="name" />
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-surface-light transition-colors"
                onClick={() => handleSort('pan')}
              >
                PAN <SortIcon column="pan" />
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                Contact
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                Assigned Team
              </th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                Status
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-semibold text-text-primary cursor-pointer hover:bg-surface-light transition-colors"
                onClick={() => handleSort('lastUpdated')}
              >
                Last Activity <SortIcon column="lastUpdated" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-overlay">
            {customers.map((customer) => {
              const isSelected = selectedIds.includes(customer.id)
              return (
              <tr
                key={customer.id}
                onClick={() => handleRowClick(customer.id)}
                className={`even:bg-surface-overlay/20 hover:bg-surface-overlay/50 active:bg-surface-overlay/70 cursor-pointer transition-colors group ${
                  isSelected ? 'bg-primary-50 even:bg-primary-50 dark:bg-primary-900/20 dark:even:bg-primary-900/20' : ''
                }`}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation()
                      toggleSelectCustomer(customer.id)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
                  />
                </td>
                <td className="px-4 py-4">
                  <div>
                    <p className="font-semibold text-text-primary group-hover:text-primary-600 group-hover:underline dark:group-hover:text-primary-400 transition-colors">{customer.fullName}</p>
                    <p className="text-sm text-text-secondary">
                      {customer.totalAssessments} assessment{customer.totalAssessments !== 1 ? 's' : ''}
                      {customer.pendingAssessments > 0 && (
                        <span className="text-amber-600 dark:text-amber-400 ml-2">
                          ({customer.pendingAssessments} pending)
                        </span>
                      )}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <code className="font-mono text-sm bg-surface-overlay px-2 py-1 rounded">
                    {customer.pan}
                  </code>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm">
                    <p className="text-text-primary truncate max-w-48">{customer.emailPrimary}</p>
                    <p className="text-text-secondary">{customer.mobilePrimary}</p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-primary-700 dark:text-primary-400">
                        {customer.assignedCAName?.split(' ').map(n => n[0]).join('') || '—'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-text-primary truncate max-w-36">
                        {customer.assignedCAName || <span className="text-text-tertiary">Unassigned</span>}
                      </p>
                      {(customer.reviewerName || customer.researcherName) && (
                        <p className="text-[11px] text-text-secondary truncate max-w-36">
                          {customer.reviewerName ? `Rev: ${customer.reviewerName}` : `Res: ${customer.researcherName}`}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  {getStatusBadge(customer.status)}
                </td>
                <td className="px-4 py-4 text-sm text-text-secondary">
                  {formatDate(customer.lastUpdated)}
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const SKELETON_ROW_COUNT = 6

/**
 * Skeleton placeholder that mirrors the CustomerTable layout while data loads.
 * Uses the `.skeleton` shimmer utility from taxhive-theme.css.
 */
export function CustomerTableSkeleton() {
  return (
    <div className="card card-elevated overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-overlay">
            <tr>
              <th className="w-12 px-4 py-3">
                <div className="skeleton h-4 w-4 rounded" />
              </th>
              {['Customer', 'PAN', 'Contact', 'Assigned CA', 'Status', 'Last Activity'].map((label) => (
                <th key={label} className="px-4 py-3 text-left">
                  <div className="skeleton h-3 w-20 rounded" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-overlay">
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
              <tr key={i}>
                <td className="px-4 py-4">
                  <div className="skeleton h-4 w-4 rounded" />
                </td>
                <td className="px-4 py-4">
                  <div className="space-y-2">
                    <div className="skeleton h-4 w-32 rounded" />
                    <div className="skeleton h-3 w-24 rounded" />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="skeleton h-6 w-28 rounded" />
                </td>
                <td className="px-4 py-4">
                  <div className="space-y-2">
                    <div className="skeleton h-4 w-40 rounded" />
                    <div className="skeleton h-3 w-24 rounded" />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <div className="skeleton h-8 w-8 rounded-full" />
                    <div className="skeleton h-3 w-20 rounded hidden xl:block" />
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="skeleton h-6 w-16 rounded-full" />
                </td>
                <td className="px-4 py-4">
                  <div className="skeleton h-3 w-20 rounded" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
